import React from "react";

/**
 * Tiny inline-link renderer. Converts a plain string with
 *   `[label](url)` markdown-style links
 * into a React fragment with real <a> tags. Bare URLs are also
 * autolinked. Everything else renders as plain text.
 *
 * This intentionally avoids a full Markdown parser — admins only need
 * "make this word a link" for announcements, not headings or lists.
 */

// `[label](url)` form is matched with optional whitespace between `]`
// and `(` — typing `[label] (url)` (with a space) is a common mistake
// and we'd rather render it as a link than as literal brackets.
// Bare URLs (http(s)://…) are also autolinked.
const LINK_PATTERN = /\[([^\]]+)\]\s*\(([^)]+)\)|(https?:\/\/[^\s<]+)/g;

const SAFE_URL = (raw: string): string | null => {
  // Trim any whitespace the admin accidentally pasted inside the parens.
  const trimmed = raw.trim();
  if (!trimmed) return null;
  // Reject obvious malicious schemes up-front.
  if (/^\s*(javascript|data|vbscript|file):/i.test(trimmed)) return null;

  // If the admin omitted the protocol (very common typo — "example.com"
  // instead of "https://example.com"), prepend https:// rather than
  // letting URL() resolve it as a relative path on the current origin
  // (which would 404 or send users to about:blank).
  const withProtocol = /^[a-z][a-z0-9+\-.]*:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;

  try {
    const u = new URL(withProtocol);
    if (u.protocol !== "http:" && u.protocol !== "https:") return null;
    return u.toString();
  } catch {
    return null;
  }
};

export const renderInlineLinks = (text: string): React.ReactNode => {
  if (!text) return null;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let keyCounter = 0;

  // IonReactRouter intercepts clicks on `<a href>` and tries to route
  // them through React Router. For external URLs there's no matching
  // route and the browser falls through to about:blank. The onClick
  // handler below explicitly opens the URL via window.open and stops
  // propagation before Ionic's anchor interceptor can fire.
  const renderLink = (label: string, safeUrl: string) => (
    <a
      key={keyCounter++}
      href={safeUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        window.open(safeUrl, "_blank", "noopener,noreferrer");
      }}
    >
      {label}
    </a>
  );

  LINK_PATTERN.lastIndex = 0;
  while ((match = LINK_PATTERN.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    // [label](url) form
    if (match[1] && match[2]) {
      const safe = SAFE_URL(match[2]);
      if (safe) {
        parts.push(renderLink(match[1], safe));
      } else {
        // Unsafe URL — render as plain text rather than dropping it
        parts.push(match[0]);
      }
    } else if (match[3]) {
      // Bare URL form
      const safe = SAFE_URL(match[3]);
      if (safe) {
        parts.push(renderLink(match[3], safe));
      } else {
        parts.push(match[0]);
      }
    }
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
};
